import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, LayoutDashboard, CalendarDays, List, PoundSterling, Settings as SettingsIcon, Plus, BookOpen, Ellipsis } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const nav = [
  { to: "/admin/dashboard", label: "Dashboard", short: "Home", icon: LayoutDashboard, end: true },
  { to: "/admin/calendar", label: "Calendar", short: "Calendar", icon: CalendarDays },
  { to: "/admin/bookings", label: "All Bookings", short: "Bookings", icon: List, end: true },
  { to: "/admin/payments", label: "Payments", short: "Payments", icon: PoundSterling },
];
const moreNav = [
  { to: "/admin/journal", label: "Journal", short: "Journal", icon: BookOpen, end: false },
  { to: "/admin/settings", label: "Settings", short: "Settings", icon: SettingsIcon, end: false },
];

const AdminShell = ({
  title,
  actions,
  children,
}: {
  title: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
}) => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();
  const onBookingPage = /^\/admin\/bookings\/.+/.test(pathname);
  const moreActive = moreNav.some((item) =>
    item.to === "/admin/journal" ? pathname.startsWith("/admin/journal") : pathname.startsWith(item.to)
  );

  useEffect(() => {
    if (!moreOpen) return;
    const onDown = (e: MouseEvent | TouchEvent) => {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) setMoreOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [moreOpen]);

  const onLogout = async () => {
    await signOut();
    navigate("/admin", { replace: true });
  };

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `font-inter text-sm tracking-wide transition-colors rounded-full px-3 py-1.5 ${
      isActive ? "text-primary bg-primary/10" : "text-white/60 hover:text-white hover:bg-white/5"
    }`;

  return (
    <div className="min-h-screen admin-bg admin-mobile text-white pb-24 lg:pb-0">
      <header className="border-b border-white/10 bg-black/60 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 h-14 md:h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-6 min-w-0">
            <Link
              to="/admin/dashboard"
              className="font-playfair tracking-widest text-sm md:text-base shrink-0"
            >
              OLIVIA · ADMIN
            </Link>
            <nav className="hidden lg:flex items-center gap-5">
              {[...nav, ...moreNav].map((item) => (
                <NavLink key={item.to} to={item.to} end={item.end} className={linkClass}>
                  {item.label}
                </NavLink>
              ))}
            </nav>
          </div>
          <div className="flex items-center gap-3 shrink-0 lg:flex">
            <span className="hidden md:inline text-xs text-white/40 font-inter truncate max-w-[160px]">
              {user?.email}
            </span>
            <button
              type="button"
              onClick={onLogout}
              className="hidden sm:inline-flex text-xs uppercase tracking-widest border border-white/20 px-3 py-2 rounded-full hover:border-primary hover:text-primary transition-colors"
            >
              Log out
            </button>
            <button
              type="button"
              className="lg:hidden p-2 text-white/80"
              aria-label="Menu"
              onClick={() => setOpen((v) => !v)}
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
        {open && (
          <nav className="lg:hidden border-t border-white/10 px-4 py-3 flex flex-col gap-3">
            {nav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={linkClass}
                onClick={() => setOpen(false)}
              >
                {item.label}
              </NavLink>
            ))}
            <button
              type="button"
              onClick={onLogout}
              className="text-left text-sm text-white/60 hover:text-primary"
            >
              Log out
            </button>
          </nav>
        )}
      </header>
      <main className="max-w-6xl mx-auto px-4 py-6 md:py-12">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6 md:mb-10 pb-5 md:pb-6 border-b border-white/5 [&>*:last-child>a]:w-full sm:[&>*:last-child>a]:w-auto">
          <div>
            <p className="font-inter text-[11px] uppercase tracking-[0.3em] text-primary/80 mb-2">
              Olivia Flemming · Bookings
            </p>
            <h1 className="font-playfair text-3xl md:text-5xl leading-tight">{title}</h1>
          </div>
          {actions}
        </div>
        {children}
      </main>
      {!onBookingPage && <Link
        to="/admin/bookings/new"
        aria-label="New booking"
        className="lg:hidden fixed right-4 bottom-[calc(5rem+env(safe-area-inset-bottom))] z-40 w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-lg shadow-black/50 flex items-center justify-center active:scale-95 transition-transform"
      >
        <Plus className="w-6 h-6" />
      </Link>}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 border-t border-white/10 bg-black/85 backdrop-blur-xl pb-[env(safe-area-inset-bottom)]">
        <div className="grid grid-cols-5 relative">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-1 py-2.5 min-h-[56px] font-inter text-[11px] ${
                  isActive ? "text-primary" : "text-white/55"
                }`
              }
            >
              <item.icon className="w-5 h-5" />
              {item.short}
            </NavLink>
          ))}
          <div className="relative" ref={moreRef}>
            <button
              type="button"
              aria-expanded={moreOpen}
              onClick={() => setMoreOpen((v) => !v)}
              className={`flex w-full flex-col items-center justify-center gap-1 py-2.5 min-h-[56px] font-inter text-[11px] ${
                moreActive ? "text-primary" : "text-white/55"
              }`}
            >
              <Ellipsis className="w-5 h-5" />
              More
            </button>
            {moreOpen && (
              <div className="absolute bottom-[calc(100%+0.5rem)] right-2 w-44 rounded-2xl border border-white/10 bg-black/95 backdrop-blur-xl shadow-xl shadow-black/60 overflow-hidden">
                {moreNav.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-4 py-3 font-inter text-sm ${
                        isActive ? "text-primary bg-primary/10" : "text-white/70"
                      }`
                    }
                    onClick={() => setMoreOpen(false)}
                  >
                    <item.icon className="w-4 h-4" />
                    {item.label}
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        </div>
      </nav>
    </div>
  );
};

export default AdminShell;
